import {
  Alert,
  Box,
  Button,
  Header,
  SegmentedControl,
  SpaceBetween,
} from "@amzn/awsui-components-console";
import React from "react";

import useAdminUserAnalytics, {
  ANALYTICS_RANGE_OPTIONS,
  AnalyticsRange,
} from "@/hooks/admin/useAdminUserAnalytics";

import AnalyticsKpiCards from "./AnalyticsKpiCards";
import DailyActivityChart from "./DailyActivityChart";
import ModelBreakdownTable from "./ModelBreakdownTable";
import UserUsageTable from "./UserUsageTable";

/** Explains why the all-time window has no daily trend to draw. */
const ALL_TIME_NO_TREND =
  "All-time figures are kept as running totals, which carry no day-by-day breakdown. Pick a fixed window to see the trend.";

/** Describes the window covered, including that its days are UTC days. */
const windowDescription = (
  rangeStart: string | null,
  rangeEnd: string | null,
  timezone: string
): string => {
  if (!rangeStart || !rangeEnd) {
    return "Everything recorded so far.";
  }
  return `${rangeStart} to ${rangeEnd}, counted in ${timezone} days (which do not line up with your local midnight).`;
};

/**
 * The admin User Analytics view: how much the assistant is being used, by whom,
 * with which models, and at what estimated cost.
 *
 * Recording began when the feature was deployed, so windows reaching further
 * back than that are complete only from that point on — stated on the page
 * rather than left for someone to infer from a suspiciously quiet chart.
 */
const UserAnalytics: React.FC = () => {
  const { data, loading, error, range, setRange, refresh } =
    useAdminUserAnalytics();

  return (
    <SpaceBetween size="l">
      <Header
        variant="h2"
        description={
          data
            ? windowDescription(data.rangeStart, data.rangeEnd, data.timezone)
            : "Select a window to summarise usage."
        }
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <SegmentedControl
              selectedId={range}
              onChange={({ detail }) =>
                setRange(detail.selectedId as AnalyticsRange)
              }
              label="Reporting window"
              options={ANALYTICS_RANGE_OPTIONS.map(({ value, label }) => ({
                id: value,
                text: label,
              }))}
            />
            <Button
              iconName="refresh"
              onClick={refresh}
              loading={loading}
              ariaLabel="Refresh analytics"
            >
              Refresh
            </Button>
          </SpaceBetween>
        }
      >
        Usage
      </Header>

      {error && (
        <Alert
          type="error"
          header="Could not load analytics"
          data-testid="analytics-error"
          action={<Button onClick={refresh}>Retry</Button>}
        >
          {error}
        </Alert>
      )}

      {data && (
        <>
          <AnalyticsKpiCards kpis={data.kpis} />
          <DailyActivityChart
            points={data.dailySeries}
            loading={loading}
            // Keyed on the response being drawn, not on the selected range.
            // The two disagree while a switch is in flight — `range` updates at
            // once, `data` still holds the last window — so gating on `range`
            // could pin this explanation to a series it does not describe. Null
            // bounds are what identify the lifetime window (see
            // windowDescription), so this asks the rendered data directly
            // whether it is the one with no per-day breakdown.
            unavailableReason={
              data.dailySeries.length === 0 && !data.rangeStart && !data.rangeEnd
                ? ALL_TIME_NO_TREND
                : undefined
            }
          />
          <ModelBreakdownTable rows={data.modelBreakdown} loading={loading} />
          <UserUsageTable rows={data.users} loading={loading} />
        </>
      )}

      {!data && !error && (
        <Box
          textAlign="center"
          padding="xxl"
          color="text-body-secondary"
          data-testid="analytics-initial-loading"
        >
          Loading usage analytics&hellip;
        </Box>
      )}
    </SpaceBetween>
  );
};

export default UserAnalytics;
